/*
 * Dār Vault — workshop ticket (Oct 2026).
 * Used by sections/dv-workshop-ticket.liquid and sections/dv-workshop-register.liquid.
 *
 * - [data-dv-ticket]: the "Admit One" ticket. Draws its grainy, slowly moving texture with WebGL
 *   (a port of the Paper Shaders "Dithering" shader, warp shape, random dithering), tilts under the
 *   pointer, and on click punches a hole in the stub, then opens the drawer, scrolls to the form,
 *   or follows its link (data-dv-action = drawer | scroll | link).
 * - [data-dv-dither]: any other canvas that should carry the same texture (the drawer header).
 * - dialog[data-dv-drawer]: the slide-out panel with the registration form and WhatsApp.
 * - form[data-dv-book]: the seat booking form. Adds the seat to the cart with the participant's
 *   answers (line item properties), then goes straight to checkout. Without JavaScript the form
 *   still posts natively and Shopify redirects to checkout (return_to).
 * Safe to load more than once; re-initialises sections in the theme editor.
 */
(function () {
  'use strict';

  if (window.DvWorkshop) {
    window.DvWorkshop.init(document);
    return;
  }

  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var mqFine = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };
  var REF_WIDTH = 741;

  /* ------------------------------------------------------------------ texture */

  var VERT = 'attribute vec2 a_pos;void main(){gl_Position=vec4(a_pos,0.,1.);}';
  var FRAG = [
    '#ifdef GL_FRAGMENT_PRECISION_HIGH',
    'precision highp float;',
    '#else',
    'precision mediump float;',
    '#endif',
    'uniform vec2 u_res;',
    'uniform float u_time;',
    'uniform float u_dpr;',
    'uniform float u_unit;',
    'uniform vec3 u_back;',
    'uniform vec3 u_front;',
    'float hash21(vec2 p){p=fract(p*vec2(.3183099,.3678794))+.1;p+=dot(p,p+19.19);return fract(p.x*p.y);}',
    'void main(){',
    '  float t=.5*u_time;',
    '  float px=.5*u_dpr;',
    '  vec2 g=(gl_FragCoord.xy-.5*u_res)/px;',
    '  vec2 cp=(floor(g)+.5)*px;',
    '  vec2 uv=(cp/u_dpr)/u_unit+.5;',
    '  uv*=.003;',
    '  for(float i=1.;i<6.;i++){',
    '    uv.x+=.6/i*cos(i*2.5*uv.y+t);',
    '    uv.y+=.6/i*cos(i*1.5*uv.x+t);',
    '  }',
    '  float shape=.15/max(.001,abs(sin(t-uv.y-uv.x)));',
    '  shape=smoothstep(.02,1.,shape);',
    '  float d=step(hash21(cp),shape);',
    '  gl_FragColor=vec4(mix(u_back,u_front,d),1.);',
    '}'
  ].join('\n');

  function hexToRgb(value, fallback) {
    var hex = String(value || '').trim().replace('#', '');
    if (hex.length === 3) hex = hex.replace(/(.)/g, '$1$1');
    if (!/^[0-9a-f]{6}$/i.test(hex)) return fallback;
    return [parseInt(hex.slice(0, 2), 16) / 255, parseInt(hex.slice(2, 4), 16) / 255, parseInt(hex.slice(4, 6), 16) / 255];
  }

  function compile(gl, type, source) {
    var shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  function Texture(canvas, palette) {
    this.canvas = canvas;
    this.palette = palette;
    this.time = 7 + Math.random() * 30;
    this.speed = 0.4;
    this.running = false;
    this.inView = false;
    this.raf = 0;
    this.last = 0;
    this.ok = this.setup();
  }

  Texture.prototype.setup = function () {
    var canvas = this.canvas;
    var gl = null;
    try {
      gl = canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false,
        powerPreference: 'low-power'
      });
    } catch (e) {
      gl = null;
    }
    if (!gl) return false;
    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return false;
    var program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;
    gl.useProgram(program);
    var buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.gl = gl;
    this.u = {
      res: gl.getUniformLocation(program, 'u_res'),
      time: gl.getUniformLocation(program, 'u_time'),
      dpr: gl.getUniformLocation(program, 'u_dpr'),
      unit: gl.getUniformLocation(program, 'u_unit'),
      back: gl.getUniformLocation(program, 'u_back'),
      front: gl.getUniformLocation(program, 'u_front')
    };
    var self = this;
    canvas.addEventListener('webglcontextlost', function (event) {
      event.preventDefault();
      self.ok = false;
      self.stop();
      var host = canvas.closest('[data-dv-ticket], .dv-wkd__stub');
      if (host) host.classList.remove('is-gl');
    });
    return true;
  };

  Texture.prototype.resize = function () {
    if (!this.ok) return;
    var w = this.canvas.offsetWidth;
    var h = this.canvas.offsetHeight;
    if (!w || !h) return;
    var dpr = Math.min(Math.max(window.devicePixelRatio || 1, 2), 3);
    var cap = 2400000;
    if (w * h * dpr * dpr > cap) dpr = Math.sqrt(cap / (w * h));
    var W = Math.round(w * dpr);
    var H = Math.round(h * dpr);
    if (this.canvas.width !== W || this.canvas.height !== H) {
      this.canvas.width = W;
      this.canvas.height = H;
    }
    this.dpr = W / w;
    this.unit = (this.unitWidth || w) / REF_WIDTH;
    this.draw();
  };

  Texture.prototype.draw = function () {
    if (!this.ok) return;
    var gl = this.gl;
    var back = hexToRgb(this.palette.back, [0.72, 0.33, 0.11]);
    var front = hexToRgb(this.palette.front, [0.96, 0.75, 0.54]);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.uniform2f(this.u.res, this.canvas.width, this.canvas.height);
    gl.uniform1f(this.u.time, this.time);
    gl.uniform1f(this.u.dpr, this.dpr || 2);
    gl.uniform1f(this.u.unit, this.unit || 1);
    gl.uniform3f(this.u.back, back[0], back[1], back[2]);
    gl.uniform3f(this.u.front, front[0], front[1], front[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  };

  Texture.prototype.loop = function (now) {
    if (!this.running) return;
    var dt = this.last ? Math.min((now - this.last) / 1000, 0.1) : 0;
    this.last = now;
    this.time += dt * this.speed;
    this.draw();
    var self = this;
    this.raf = requestAnimationFrame(function (t) {
      self.loop(t);
    });
  };

  Texture.prototype.start = function () {
    if (!this.ok) return;
    if (mqReduce.matches || document.hidden || !this.inView) {
      this.stop();
      this.draw();
      return;
    }
    if (this.running) return;
    this.running = true;
    this.last = 0;
    var self = this;
    this.raf = requestAnimationFrame(function (t) {
      self.loop(t);
    });
  };

  Texture.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  Texture.prototype.destroy = function () {
    this.stop();
    if (this.gl && this.gl.getExtension('WEBGL_lose_context')) {
      try {
        this.gl.getExtension('WEBGL_lose_context').loseContext();
      } catch (e) {}
    }
    this.ok = false;
  };

  /* -------------------------------------------------------------------- sound */

  var audioCtx = null;

  function burst(ctx, at, gainValue, decay, frequency, q) {
    var length = Math.ceil(0.05 * ctx.sampleRate);
    var buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    var data = buffer.getChannelData(0);
    for (var i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    var source = ctx.createBufferSource();
    source.buffer = buffer;
    var filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = frequency;
    filter.Q.value = q;
    var gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(gainValue, at + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(at);
    source.stop(at + decay + 0.02);
  }

  function punchSound() {
    try {
      var Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return;
      if (!audioCtx) audioCtx = new Ctor();
      if (audioCtx.state === 'suspended') audioCtx.resume();
      var now = audioCtx.currentTime;
      burst(audioCtx, now, 0.22, 0.035, 3200, 1.1);
      burst(audioCtx, now + 0.045, 0.16, 0.055, 1800, 0.9);
    } catch (e) {}
  }

  /* ------------------------------------------------------------------- drawer */

  var openDrawerEl = null;
  var lastOpener = null;

  function startDrawerTextures(dialog) {
    var canvases = dialog.querySelectorAll('canvas[data-dv-dither]');
    for (var i = 0; i < canvases.length; i++) {
      var tex = canvases[i].__dvTexture;
      if (tex) {
        tex.inView = true;
        tex.resize();
        tex.start();
      }
    }
  }

  function stopDrawerTextures(dialog) {
    var canvases = dialog.querySelectorAll('canvas[data-dv-dither]');
    for (var i = 0; i < canvases.length; i++) {
      var tex = canvases[i].__dvTexture;
      if (tex) {
        tex.inView = false;
        tex.stop();
      }
    }
  }

  function openDrawer(dialog, opener) {
    if (!dialog) return false;
    if (typeof dialog.showModal !== 'function') return false;
    if (dialog.open) return true;
    lastOpener = opener || document.activeElement;
    dialog.classList.remove('is-closing');
    dialog.showModal();
    openDrawerEl = dialog;
    document.documentElement.classList.add('dv-wk-locked');
    startDrawerTextures(dialog);
    var focusTarget = dialog.querySelector('[data-dv-drawer-title]');
    if (focusTarget) {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (e) {
        focusTarget.focus();
      }
    }
    return true;
  }

  function closeDrawer(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    var finish = function () {
      dialog.classList.remove('is-closing');
      if (dialog.open) dialog.close();
      document.documentElement.classList.remove('dv-wk-locked');
      stopDrawerTextures(dialog);
      openDrawerEl = null;
      if (lastOpener && typeof lastOpener.focus === 'function') {
        try {
          lastOpener.focus({ preventScroll: true });
        } catch (e) {}
      }
    };
    if (mqReduce.matches) {
      finish();
      return;
    }
    dialog.classList.add('is-closing');
    var done = false;
    var onEnd = function () {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', onEnd);
      finish();
    };
    dialog.addEventListener('animationend', onEnd);
    setTimeout(onEnd, 420);
  }

  function setupDrawer(dialog) {
    if (dialog.__dvDrawer) return;
    dialog.__dvDrawer = true;
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeDrawer(dialog);
    });
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeDrawer(dialog);
      var closer = event.target.closest && event.target.closest('[data-dv-drawer-close]');
      if (closer) {
        event.preventDefault();
        closeDrawer(dialog);
      }
    });
    var canvases = dialog.querySelectorAll('canvas[data-dv-dither]');
    for (var i = 0; i < canvases.length; i++) setupDither(canvases[i]);
  }

  /* ------------------------------------------------------------------- ticket */

  function paletteOf(el) {
    var style = getComputedStyle(el);
    return {
      back: style.getPropertyValue('--dv-tk-back'),
      front: style.getPropertyValue('--dv-tk-front')
    };
  }

  function setupDither(canvas) {
    if (canvas.__dvTexture) return canvas.__dvTexture;
    var host = canvas.closest('[data-dv-palette]') || canvas.parentElement;
    var tex = new Texture(canvas, paletteOf(host));
    canvas.__dvTexture = tex;
    if (tex.ok) {
      host.classList.add('is-gl');
      tex.unitWidth = parseFloat(canvas.getAttribute('data-dv-unit-width')) || 0;
      tex.resize();
    }
    return tex;
  }

  function Ticket(root) {
    this.root = root;
    this.button = root.querySelector('[data-dv-ticket-trigger]');
    this.card = root.querySelector('.dv-tk__card');
    this.glare = root.querySelector('.dv-tk__glare');
    this.canvas = root.querySelector('canvas.dv-tk__tex');
    this.action = root.getAttribute('data-dv-action') || 'drawer';
    this.sound = root.getAttribute('data-dv-sound') === 'true';
    this.cleanups = [];
    this.busy = false;
    this.texture = null;

    if (this.canvas) {
      this.texture = setupDither(this.canvas);
      if (this.texture.ok) this.watch();
    }
    this.bindTilt();
    this.bindClick();
  }

  Ticket.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.cleanups.push(function () {
      target.removeEventListener(type, handler, options);
    });
  };

  Ticket.prototype.watch = function () {
    var self = this;
    var tex = this.texture;
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          tex.inView = entries[0] && entries[0].isIntersecting;
          tex.start();
        },
        { rootMargin: '80px' }
      );
      io.observe(this.root);
      this.cleanups.push(function () {
        io.disconnect();
      });
    } else {
      tex.inView = true;
      tex.start();
    }
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        tex.resize();
      });
      ro.observe(this.canvas);
      this.cleanups.push(function () {
        ro.disconnect();
      });
    } else {
      this.on(window, 'resize', function () {
        tex.resize();
      });
    }
    this.on(document, 'visibilitychange', function () {
      tex.start();
    });
    var onMotion = function () {
      tex.start();
    };
    if (mqReduce.addEventListener) {
      mqReduce.addEventListener('change', onMotion);
      this.cleanups.push(function () {
        mqReduce.removeEventListener('change', onMotion);
      });
    }
    self.root.classList.add('is-ready');
  };

  Ticket.prototype.bindTilt = function () {
    var button = this.button;
    var glare = this.glare;
    var root = this.root;
    if (!button) return;
    var maxTilt = 9;
    var hovering = false;
    var self = this;
    this.on(button, 'pointerenter', function (event) {
      if (event.pointerType !== 'mouse' || mqReduce.matches || !mqFine.matches) return;
      hovering = true;
      root.classList.add('is-hover');
    });
    this.on(button, 'pointermove', function (event) {
      if (!hovering) return;
      var rect = button.getBoundingClientRect();
      var dx = (event.clientX - rect.left) / rect.width - 0.5;
      var dy = (event.clientY - rect.top) / rect.height - 0.5;
      button.style.transform =
        'rotateX(' + (-dy * 2 * maxTilt).toFixed(2) + 'deg) rotateY(' + (dx * 2 * maxTilt).toFixed(2) + 'deg) scale(1.02)';
      if (glare) {
        glare.style.background =
          'radial-gradient(38% 55% at ' +
          ((dx + 0.5) * 100).toFixed(1) +
          '% ' +
          ((dy + 0.5) * 100).toFixed(1) +
          '%, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)';
      }
    });
    this.on(button, 'pointerleave', function () {
      if (!hovering) return;
      hovering = false;
      root.classList.remove('is-hover');
      button.style.transform = '';
      if (glare) glare.style.background = '';
    });
    self.resetTilt = function () {
      hovering = false;
      root.classList.remove('is-hover');
      button.style.transform = '';
      if (glare) glare.style.background = '';
    };
  };

  Ticket.prototype.punch = function () {
    var root = this.root;
    if (this.sound) punchSound();
    if (!root.classList.contains('is-punched')) {
      root.classList.add('is-punched');
    }
    root.classList.remove('is-pressed');
    void root.offsetWidth;
    root.classList.add('is-pressed');
    setTimeout(function () {
      root.classList.remove('is-pressed');
    }, 420);
  };

  Ticket.prototype.bindClick = function () {
    var self = this;
    var button = this.button;
    if (!button) return;
    this.on(button, 'click', function (event) {
      var action = self.action;
      var href = button.getAttribute('href');
      if (action === 'drawer') {
        var dialog = document.getElementById(button.getAttribute('aria-controls') || '');
        if (!dialog || typeof dialog.showModal !== 'function') {
          var fallback = button.getAttribute('data-dv-fallback');
          if (fallback) window.location.href = fallback;
          return;
        }
        event.preventDefault();
        if (self.busy) return;
        self.busy = true;
        self.punch();
        setTimeout(
          function () {
            self.busy = false;
            if (self.resetTilt) self.resetTilt();
            openDrawer(dialog, button);
          },
          mqReduce.matches ? 0 : 360
        );
        return;
      }
      if (!href) return;
      if (action === 'scroll' && href.charAt(0) === '#') {
        var target = document.getElementById(href.slice(1));
        if (!target) return;
        event.preventDefault();
        self.punch();
        setTimeout(
          function () {
            target.scrollIntoView({ behavior: mqReduce.matches ? 'auto' : 'smooth', block: 'start' });
            // With a mouse, put the cursor in the first field. On touch screens focus the section itself,
            // so the phone keyboard doesn't pop up over the form while the page scrolls.
            var focusable = (mqFine.matches && target.querySelector('[data-dv-focus]')) || target;
            if (focusable && typeof focusable.focus === 'function') {
              try {
                focusable.focus({ preventScroll: true });
              } catch (e) {}
            }
          },
          mqReduce.matches ? 0 : 260
        );
        return;
      }
      if (action === 'link') {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        self.punch();
        setTimeout(
          function () {
            window.location.href = href;
          },
          mqReduce.matches ? 0 : 380
        );
      }
    });
  };

  Ticket.prototype.destroy = function () {
    for (var i = 0; i < this.cleanups.length; i++) this.cleanups[i]();
    this.cleanups = [];
    if (this.texture) this.texture.destroy();
    if (this.canvas) this.canvas.__dvTexture = null;
  };

  /* --------------------------------------------------------------- booking */

  function setStatus(form, message) {
    var status = form.querySelector('[data-dv-status]');
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-dv-book]')) return;
    if (!window.fetch || !window.FormData) return;
    event.preventDefault();
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
    var button = form.querySelector('[type="submit"]');
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
    setStatus(form, '');
    var action = (form.getAttribute('action') || '/cart/add').split('?')[0].replace(/\/$/, '');
    var cartBase = action.replace(/\/add$/, '');
    var checkout = form.getAttribute('data-dv-checkout') || '/checkout';
    var idInput = form.querySelector('[name="id"]');
    var variantId = idInput ? String(idInput.value) : '';
    var headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    var readJson = function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
    };
    // One seat per booking: first take out any seat already in the cart (for example when someone
    // comes back from checkout to correct their name), then add the seat with the new answers.
    fetch(cartBase + '.js', { headers: headers, credentials: 'same-origin' })
      .then(readJson)
      .then(function (cart) {
        var keys = (cart.items || [])
          .filter(function (item) {
            return String(item.variant_id) === variantId;
          })
          .map(function (item) {
            return item.key;
          });
        return keys.reduce(function (chain, key) {
          return chain.then(function () {
            return fetch(cartBase + '/change.js', {
              method: 'POST',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: key, quantity: 0 }),
              credentials: 'same-origin'
            }).then(readJson);
          });
        }, Promise.resolve());
      })
      .catch(function () {})
      .then(function () {
        return fetch(action + '.js', {
          method: 'POST',
          headers: headers,
          body: new FormData(form),
          credentials: 'same-origin'
        }).then(readJson);
      })
      .then(function () {
        try {
          if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
            Shopify.analytics.publish('dv_workshop_checkout', { source: form.getAttribute('data-dv-book') || '' });
          }
        } catch (e) {}
        window.location.href = checkout;
      })
      .catch(function (error) {
        if (button) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
        var message = (error && (error.description || error.message)) || form.getAttribute('data-dv-error') || '';
        setStatus(form, message);
      });
  }

  /* ------------------------------------------------------------------ setup */

  function init(scope) {
    var root = scope || document;
    var tickets = root.querySelectorAll('[data-dv-ticket]');
    for (var i = 0; i < tickets.length; i++) {
      if (!tickets[i].__dvTicket) tickets[i].__dvTicket = new Ticket(tickets[i]);
    }
    var drawers = root.querySelectorAll('dialog[data-dv-drawer]');
    for (var j = 0; j < drawers.length; j++) setupDrawer(drawers[j]);
    var openers = root.querySelectorAll('[data-dv-drawer-open]');
    for (var k = 0; k < openers.length; k++) {
      if (openers[k].__dvOpener) continue;
      openers[k].__dvOpener = true;
      openers[k].addEventListener('click', function (event) {
        var dialog = document.getElementById(this.getAttribute('aria-controls') || '');
        if (openDrawer(dialog, this)) event.preventDefault();
      });
    }
    reopenAfterPost(root);
  }

  // A native contact form reloads the page and lands on #<form id>. Reopen its drawer.
  function reopenAfterPost(root) {
    var hash = window.location.hash ? window.location.hash.slice(1) : '';
    if (!hash || !/^[A-Za-z0-9_-]+$/.test(hash)) return;
    var form = document.getElementById(hash);
    if (!form || !root.contains(form)) return;
    var dialog = form.closest('dialog[data-dv-drawer]');
    if (dialog && !dialog.open) openDrawer(dialog, null);
  }

  function destroy(scope) {
    var tickets = (scope || document).querySelectorAll('[data-dv-ticket]');
    for (var i = 0; i < tickets.length; i++) {
      if (tickets[i].__dvTicket) {
        tickets[i].__dvTicket.destroy();
        tickets[i].__dvTicket = null;
      }
    }
    if (openDrawerEl && (scope || document).contains(openDrawerEl)) {
      if (openDrawerEl.open) openDrawerEl.close();
      document.documentElement.classList.remove('dv-wk-locked');
      openDrawerEl = null;
    }
  }

  document.addEventListener('submit', onSubmit);

  // Coming back from checkout with the Back button can restore the page as it was left,
  // with the booking button still waiting. Make it usable again.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    var busy = document.querySelectorAll('form[data-dv-book] [aria-busy="true"]');
    for (var i = 0; i < busy.length; i++) {
      busy[i].removeAttribute('aria-busy');
      busy[i].disabled = false;
    }
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  window.DvWorkshop = { init: init, destroy: destroy, open: openDrawer, close: closeDrawer };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
